import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IoGiftOutline, IoPlayOutline, IoRocketOutline } from "react-icons/io5";
import { normalizeLanguage } from "../../services/language";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";

const LOCAL_FRIENDS_COPY = Object.freeze({
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Назад в лобби",
  },
  title: {
    es: "Jugar con amigos",
    en: "Play with friends",
    de: "Mit Freunden spielen",
    ru: "Играть с друзьями",
  },
  subtitle: {
    es: "Configura una partida local en el mismo dispositivo.",
    en: "Set up a local match on the same device.",
    de: "Richte eine lokale Partie auf demselben Gerät ein.",
    ru: "Настрой локальную партию на одном устройстве.",
  },
  scienceModeTitle: {
    es: "Modo Ciencia",
    en: "Science mode",
    de: "Wissenschaftsmodus",
    ru: "Режим «Наука»",
  },
  scienceModeSubtitle: {
    es: "La estrategia lo es todo.",
    en: "Strategy is everything.",
    de: "Strategie ist alles.",
    ru: "Стратегия решает всё.",
  },
  scienceModeDetail: {
    es: "Juega en el mismo dispositivo.",
    en: "Play on the same device.",
    de: "Spiele auf demselben Gerät.",
    ru: "Играй на одном устройстве.",
  },
  chanceModeTitle: {
    es: "Modo Azar",
    en: "Chance mode",
    de: "Zufallsmodus",
    ru: "Режим случайности",
  },
  chanceModeSubtitle: {
    es: "La suerte decide el camino.",
    en: "Luck decides the path.",
    de: "Das Glück bestimmt den Weg.",
    ru: "Удача выбирает путь.",
  },
  chanceModeDetail: {
    es: "Dados y turnos locales.",
    en: "Dice and local turns.",
    de: "Würfel und lokale Züge.",
    ru: "Кубики и локальные ходы.",
  },
  cienzarModeTitle: {
    es: "Modo CIENZAR",
    en: "CIENZAR mode",
    de: "CIENZAR-Modus",
    ru: "Режим CIENZAR",
  },
  cienzarModeSubtitle: {
    es: "Estrategia y suerte juntas.",
    en: "Strategy and luck together.",
    de: "Strategie und Glück vereint.",
    ru: "Стратегия и удача вместе.",
  },
  cienzarModeDetail: {
    es: "El modo completo entre amigos.",
    en: "The complete mode among friends.",
    de: "Der vollständige Modus unter Freunden.",
    ru: "Полный режим для игры с друзьями.",
  },
  scienceVsAzarModeTitle: {
    es: "Modo Ciencia V/S Azar",
    en: "Science V/S Chance mode",
    de: "Wissenschaft V/S Zufall-Modus",
    ru: "Science V/S Chance mode",
  },
  scienceVsAzarModeSubtitle: {
    es: "Roles fijos para tus amigos.",
    en: "Fixed roles for your friends.",
    de: "Feste Rollen fuer deine Freunde.",
    ru: "Fixed roles for your friends.",
  },
  scienceVsAzarModeDetail: {
    es: "Al comenzar se elige quien juega ciencia y quien juega azar.",
    en: "At start, players are assigned science or chance.",
    de: "Beim Start werden Wissenschaft oder Zufall zugewiesen.",
    ru: "At start, players are assigned science or chance.",
  },
  playersTitle: {
    es: "Jugadores",
    en: "Players",
    de: "Spieler",
    ru: "Игроки",
  },
  playersDescription: {
    es: "Elige la cantidad de jugadores locales",
    en: "Choose the number of local players",
    de: "Wähle die Anzahl der lokalen Spieler",
    ru: "Выбери количество локальных игроков",
  },
  oneVsOne: {
    es: "1 vs 1",
    en: "1 vs 1",
    de: "1 gegen 1",
    ru: "1 на 1",
  },
  oneVsTwo: {
    es: "1 vs 2",
    en: "1 vs 2",
    de: "1 gegen 2",
    ru: "1 на 2",
  },
  oneVsThree: {
    es: "1 vs 3",
    en: "1 vs 3",
    de: "1 gegen 3",
    ru: "1 на 3",
  },
  oneLocalFriend: {
    es: "Un amigo local",
    en: "One local friend",
    de: "Ein lokaler Freund",
    ru: "Один локальный друг",
  },
  twoLocalFriends: {
    es: "Dos amigos locales",
    en: "Two local friends",
    de: "Zwei lokale Freunde",
    ru: "Два локальных друга",
  },
  threeLocalFriends: {
    es: "Tres amigos locales",
    en: "Three local friends",
    de: "Drei lokale Freunde",
    ru: "Три локальных друга",
  },
  timePerTurnTitle: {
    es: "Tiempo por turno",
    en: "Time per turn",
    de: "Zeit pro Zug",
    ru: "Время на ход",
  },
  timePerTurnDescription: {
    es: "Elige el tiempo para cada turno",
    en: "Choose the time for each turn",
    de: "Wähle die Zeit für jeden Zug",
    ru: "Выбери время для каждого хода",
  },
  minutes: {
    es: "{value} min",
    en: "{value} min",
    de: "{value} Min.",
    ru: "{value} мин",
  },
  localNamesTitle: {
    es: "Nombres locales",
    en: "Local names",
    de: "Lokale Namen",
    ru: "Локальные имена",
  },
  localNamesDescription: {
    es: "Escribe los nombres de tus amigos",
    en: "Enter your friends' names",
    de: "Gib die Namen deiner Freunde ein",
    ru: "Введи имена своих друзей",
  },
  localPlayerLabel: {
    es: "Jugador {number}",
    en: "Player {number}",
    de: "Spieler {number}",
    ru: "Игрок {number}",
  },
  localPlayerPlaceholder: {
    es: "Nombre jugador {number}",
    en: "Player {number} name",
    de: "Name von Spieler {number}",
    ru: "Имя игрока {number}",
  },
  completeLocalNames: {
    es: "Completa todos los nombres para crear la partida.",
    en: "Complete all names to create the match.",
    de: "Fülle alle Namen aus, um die Partie zu erstellen.",
    ru: "Заполни все имена, чтобы создать партию.",
  },
  goToShop: {
    es: "Ir a la tienda",
    en: "Go to shop",
    de: "Zum Shop",
    ru: "В магазин",
  },
  shopDescription: {
    es: "Consigue nuevos estilos para tus partidas",
    en: "Get new styles for your matches",
    de: "Hol dir neue Designs für deine Partien",
    ru: "Получи новые стили для своих партий",
  },
  create: {
    es: "Crear",
    en: "Create",
    de: "Erstellen",
    ru: "Создать",
  },
  creating: {
    es: "Creando...",
    en: "Creating...",
    de: "Wird erstellt...",
    ru: "Создание...",
  },
  championshipTitle: {
    es: "Campeonato Mundial XIMA 2026",
    en: "XIMA World Championship 2026",
    de: "XIMA-Weltmeisterschaft 2026",
    ru: "Чемпионат мира по XIMA 2026",
  },
  championshipDescription: {
    es: "Entrena localmente y domina cada modo.",
    en: "Train locally and master every mode.",
    de: "Trainiere lokal und meistere jeden Modus.",
    ru: "Тренируйся локально и осваивай каждый режим.",
  },
  modeSectionLabel: {
    es: "Modo de juego local",
    en: "Local game mode",
    de: "Lokaler Spielmodus",
    ru: "Локальный режим игры",
  },
  missingNamesTitle: {
    es: "Faltan nombres locales",
    en: "Local names are missing",
    de: "Lokale Namen fehlen",
    ru: "Не хватает локальных имён",
  },
  missingNamesDescription: {
    es: "Escribe el nombre de cada jugador local antes de crear la partida.",
    en: "Enter each local player's name before creating the match.",
    de: "Gib den Namen jedes lokalen Spielers ein, bevor du die Partie erstellst.",
    ru: "Введи имя каждого локального игрока перед созданием партии.",
  },
  missingLocalMatchId: {
    es: "No se recibió el identificador de la partida local.",
    en: "The local match identifier was not received.",
    de: "Die Kennung der lokalen Partie wurde nicht empfangen.",
    ru: "Идентификатор локальной партии не был получен.",
  },
  createErrorTitle: {
    es: "No se pudo crear la partida local",
    en: "The local match could not be created",
    de: "Die lokale Partie konnte nicht erstellt werden",
    ru: "Не удалось создать локальную партию",
  },
  createErrorDescription: {
    es: "Intenta crear la partida con amigos nuevamente.",
    en: "Try creating the match with friends again.",
    de: "Versuche erneut, die Partie mit Freunden zu erstellen.",
    ru: "Попробуй снова создать партию с друзьями.",
  },
});

const LOCAL_FRIENDS_MODE_OPTIONS = [
  {
    id: "free",
    localMode: "localfree",
    titleKey: "scienceModeTitle",
    subtitleKey: "scienceModeSubtitle",
    detailKey: "scienceModeDetail",
    icon: "/mode-icons/free.png",
    className: "xima-create-mode-science",
  },
  {
    id: "dice",
    localMode: "localdice",
    titleKey: "chanceModeTitle",
    subtitleKey: "chanceModeSubtitle",
    detailKey: "chanceModeDetail",
    icon: "/mode-icons/dice.png",
    className: "xima-create-mode-luck",
  },
  {
    id: "cienzar",
    localMode: "localcienzar",
    titleKey: "cienzarModeTitle",
    subtitleKey: "cienzarModeSubtitle",
    detailKey: "cienzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-cienzar",
  },
  {
    id: "sciencevsazar",
    localMode: "localsciencevsazar",
    titleKey: "scienceVsAzarModeTitle",
    subtitleKey: "scienceVsAzarModeSubtitle",
    detailKey: "scienceVsAzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-sciencevsazar",
  },
];

const LOCAL_FRIENDS_PLAYER_OPTIONS = [
  {
    value: 2,
    labelKey: "oneVsOne",
    detailKey: "oneLocalFriend",
    icon: "/mode-icons/players-1v1.png",
  },
  {
    value: 3,
    labelKey: "oneVsTwo",
    detailKey: "twoLocalFriends",
    icon: "/mode-icons/players-1v2.png",
  },
  {
    value: 4,
    labelKey: "oneVsThree",
    detailKey: "threeLocalFriends",
    icon: "/mode-icons/players-1v3.png",
  },
];

const LOCAL_FRIENDS_TIME_OPTIONS = [1, 2, 5, 10];
const PLAY_LOCAL_FRIENDS_PREFERENCES_STORAGE_KEY = "xima_play_local_friends_preferences_v1";
const PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES = {
  mode: "cienzar",
  players: 2,
  turnTime: 5,
  localPlayers: ["", "", ""],
};

const clampPlayers = (value) => Math.max(2, Math.min(4, Number(value) || 2));
const clampLocalFriendsZoom = (value) => Math.min(1.08, Math.max(0.76, value));

const getLocalFriendsCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = LOCAL_FRIENDS_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const normalizeLocalFriendsMode = (value) =>
  LOCAL_FRIENDS_MODE_OPTIONS.some((option) => option.id === value)
    ? value
    : PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES.mode;

const normalizeLocalFriendsTime = (value) => {
  const parsed = Number(value);
  return LOCAL_FRIENDS_TIME_OPTIONS.includes(parsed)
    ? parsed
    : PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES.turnTime;
};

const normalizeLocalPlayerNames = (value) => {
  const source = Array.isArray(value) ? value : [];
  const names = source.slice(0, 3).map((name) => String(name || "").trim());
  while (names.length < 3) names.push("");
  return names;
};

const readPlayLocalFriendsPreferences = () => {
  if (typeof window === "undefined") {
    return PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES;
  }

  try {
    const rawValue = window.localStorage.getItem(PLAY_LOCAL_FRIENDS_PREFERENCES_STORAGE_KEY);
    if (!rawValue) {
      return PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES;
    }

    return {
      mode: normalizeLocalFriendsMode(parsed.mode),
      players: clampPlayers(parsed.players),
      turnTime: normalizeLocalFriendsTime(parsed.turnTime),
      localPlayers: normalizeLocalPlayerNames(parsed.localPlayers),
    };
  } catch {
    return PLAY_LOCAL_FRIENDS_DEFAULT_PREFERENCES;
  }
};

const writePlayLocalFriendsPreferences = (preferences) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      mode: normalizeLocalFriendsMode(preferences?.mode),
      players: clampPlayers(preferences?.players),
      turnTime: normalizeLocalFriendsTime(preferences?.turnTime),
      localPlayers: normalizeLocalPlayerNames(preferences?.localPlayers),
    };
    window.localStorage.setItem(
      PLAY_LOCAL_FRIENDS_PREFERENCES_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore storage failures.
  }
};

const resolveLocalMode = (baseMode) => {
  const option = LOCAL_FRIENDS_MODE_OPTIONS.find((entry) => entry.id === baseMode);
  return option?.localMode || "localcienzar";
};

const PlayLocalFriends = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = useInterfaceLanguage(outletContext.language);
  const localFriendsText = useCallback(
    (key, values) => getLocalFriendsCopy(language, key, values),
    [language],
  );
  const screenRef = useRef(null);
  const storedPreferences = useMemo(readPlayLocalFriendsPreferences, []);
  const [mode, setMode] = useState(storedPreferences.mode);
  const [players, setPlayers] = useState(storedPreferences.players);
  const [turnTime, setTurnTime] = useState(storedPreferences.turnTime);
  const [localPlayers, setLocalPlayers] = useState(storedPreferences.localPlayers);
  const [interfaceZoom, setInterfaceZoom] = useState(1);
  const [startingGame, setStartingGame] = useState(false);

  const requiredLocalPlayers = Math.max(1, clampPlayers(players) - 1);
  const activeLocalPlayers = localPlayers.slice(0, requiredLocalPlayers);
  const localNamesComplete = activeLocalPlayers.every((name) => String(name || "").trim());

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return undefined;

    const handleWheelZoom = (event) => {
      if (!event.ctrlKey) return;

      event.preventDefault();
      setInterfaceZoom((currentZoom) => {
        const nextZoom = currentZoom + (event.deltaY < 0 ? 0.05 : -0.05);
        return Number(clampLocalFriendsZoom(nextZoom).toFixed(2));
      });
    };

    screen.addEventListener("wheel", handleWheelZoom, { passive: false });

    return () => {
      screen.removeEventListener("wheel", handleWheelZoom);
    };
  }, []);

  useEffect(() => {
    writePlayLocalFriendsPreferences({
      mode,
      players,
      turnTime,
      localPlayers,
    });
  }, [mode, players, turnTime, localPlayers]);

  const updateLocalPlayerName = (index, value) => {
    setLocalPlayers((current) => {
      const next = normalizeLocalPlayerNames(current);
      next[index] = String(value || "");
      return next;
    });
  };

  const startLocalFriendsGame = async () => {
    if (startingGame) return;

    const selectedBaseMode = normalizeLocalFriendsMode(mode);
    const selectedMode = resolveLocalMode(selectedBaseMode);
    const selectedPlayers = clampPlayers(players);
    const selectedTime = normalizeLocalFriendsTime(turnTime);
    const selectedLocalPlayers = normalizeLocalPlayerNames(localPlayers)
      .slice(0, Math.max(1, selectedPlayers - 1))
      .map((name) => String(name || "").trim());

    if (selectedLocalPlayers.length !== selectedPlayers - 1 || selectedLocalPlayers.some((name) => !name)) {
      notification.warning({
        message: localFriendsText("missingNamesTitle"),
        description: localFriendsText("missingNamesDescription"),
      });
      return;
    }

    try {
      setStartingGame(true);
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        mode: selectedMode,
        memberSize: selectedPlayers,
        havingTime: selectedTime,
        localPlayers: selectedLocalPlayers,
        duration: 1,
        startingTime: new Date().toISOString(),
        isQuickMatch: false,
        isIndependentMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentRewardValue: 0,
        tournamentWinnerPrizeValue: 0,
      });

      const created = response?.data || null;
      const tournamentId = String(created?._id || "").trim();

      if (!tournamentId) {
        throw new Error(localFriendsText("missingLocalMatchId"));
      }

      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "local-friends",
          source: "lobbyLocalFriendsInterface",
          localFriendsPreferences: {
            mode: selectedMode,
            memberSize: selectedPlayers,
            havingTime: selectedTime,
            localPlayers: selectedLocalPlayers,
          },
        },
      });
    } catch (error) {
      console.error("Error starting local friends game:", error);
      notification.error({
        message: localFriendsText("createErrorTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          localFriendsText("createErrorDescription"),
      });
    } finally {
      setStartingGame(false);
    }
  };

  const openChampionship = () => {
    navigate("/game/play");
  };

  return (
    <main
      ref={screenRef}
      className="xima-create-game-screen xima-local-friends-game-screen"
      style={{ "--xima-create-zoom": String(interfaceZoom) }}
    >
      <div className="xima-create-game-overlay" aria-hidden="true" />
      <div className="xima-create-game-shell">
        <header className="xima-create-game-header">
          <div className="xima-create-game-logo" aria-label="XIMA">
            XiMA
          </div>
          <button
            type="button"
            onClick={() => navigate("/game/lobby")}
            className="xima-create-back-button"
          >
            <span aria-hidden="true">&lt;</span>
            {localFriendsText("backToLobby")}
          </button>
          <div className="xima-create-game-title">
            <h1>{localFriendsText("title")}</h1>
            <p>{localFriendsText("subtitle")}</p>
          </div>
        </header>

        <div className="xima-create-controls-stage">
          <div className="xima-create-game-hero-space" aria-hidden="true" />

          <section className="xima-create-mode-grid" aria-label={localFriendsText("modeSectionLabel")}>
            {LOCAL_FRIENDS_MODE_OPTIONS.map((option) => {
              const selected = mode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMode(option.id)}
                  className={`xima-create-mode-card ${option.className}${
                    selected ? " xima-create-selected" : ""
                  }`}
                  aria-pressed={selected}
                >
                  <span className="xima-create-mode-icon">
                    <img
                      src={option.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="xima-create-mode-copy">
                    <strong>{localFriendsText(option.titleKey)}</strong>
                    <small>{localFriendsText(option.subtitleKey)}</small>
                    <small>{localFriendsText(option.detailKey)}</small>
                  </span>
                </button>
              );
            })}
          </section>

          <section className="xima-create-options-grid xima-local-friends-options-grid">
            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{localFriendsText("playersTitle")}</strong>
                <span>{localFriendsText("playersDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-player-grid">
                {LOCAL_FRIENDS_PLAYER_OPTIONS.map((option) => {
                  const selected = Number(players) === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPlayers(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <strong>{localFriendsText(option.labelKey)}</strong>
                      <small>{localFriendsText(option.detailKey)}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{localFriendsText("timePerTurnTitle")}</strong>
                <span>{localFriendsText("timePerTurnDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-time-grid">
                {LOCAL_FRIENDS_TIME_OPTIONS.map((value) => {
                  const selected = Number(turnTime) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTurnTime(value)}
                      className={`xima-create-choice-button xima-create-time-button${
                        selected ? " xima-create-selected" : ""
                      }`}
                      aria-pressed={selected}
                    >
                      <strong>{localFriendsText("minutes", { value })}</strong>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel xima-local-names-panel">
              <div className="xima-create-panel-heading">
                <strong>{localFriendsText("localNamesTitle")}</strong>
                <span>{localFriendsText("localNamesDescription")}</span>
              </div>
              <div className="xima-local-names-grid">
                {Array.from({ length: requiredLocalPlayers }, (_, index) => (
                  <label key={`local-friend-name-${index + 2}`} className="xima-local-name-field">
                    <span>{localFriendsText("localPlayerLabel", { number: index + 2 })}</span>
                    <input
                      type="text"
                      value={localPlayers[index] || ""}
                      onChange={(event) => updateLocalPlayerName(index, event.target.value)}
                      placeholder={localFriendsText("localPlayerPlaceholder", { number: index + 2 })}
                      className="xima-local-name-input"
                      maxLength={36}
                    />
                  </label>
                ))}
              </div>
              {!localNamesComplete ? (
                <div className="xima-local-names-warning">
                  {localFriendsText("completeLocalNames")}
                </div>
              ) : null}
            </div>
          </section>

          <section className="xima-create-action-grid">
            <button
              type="button"
              onClick={() => navigate("/game/store?fromLobbySource=store")}
              className="xima-create-promo-card xima-create-store-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoGiftOutline />
              </span>
              <span>
                <strong>{localFriendsText("goToShop")}</strong>
                <small>{localFriendsText("shopDescription")}</small>
              </span>
              <img src="/store/xima-board/xima-board-0.png" alt="" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={startLocalFriendsGame}
              className="xima-create-submit-button xima-local-friends-submit-button"
              disabled={startingGame || !localNamesComplete}
              aria-busy={startingGame}
            >
              <span aria-hidden="true">
                <IoPlayOutline />
              </span>
              {startingGame ? localFriendsText("creating") : localFriendsText("create")}
            </button>

            <button
              type="button"
              onClick={openChampionship}
              className="xima-create-promo-card xima-create-championship-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoRocketOutline />
              </span>
              <span>
                <strong>{localFriendsText("championshipTitle")}</strong>
                <small>{localFriendsText("championshipDescription")}</small>
              </span>
            </button>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PlayLocalFriends;
